/**
 * 하트 이코노미 타입 정의
 *
 * Silver Connect 하트 시스템의 모든 타입을 정의합니다.
 * 시니어가 이해할 수 있는 명확한 네이밍을 사용합니다.
 *
 * @author 프론트맨 (Frontend Developer)
 * @based-on 조너선아이브 디자인
 */

// ============================================================================
// Reward Types (리워드 관련)
// ============================================================================

/**
 * 리워드 타입
 * - signup: 신규 가입 리워드
 * - profile: 프로필 완성 리워드
 */
export type RewardType = 'signup' | 'profile';

/**
 * 리워드 정보
 */
export interface Reward {
  /** 리워드 타입 */
  type: RewardType;
  /** 지급되는 하트 개수 */
  hearts: number;
  /** 사용자에게 표시할 메시지 */
  message: string;
}

// ============================================================================
// Heart Action Types (하트 소비 액션)
// ============================================================================

/**
 * 하트 소비 액션 타입
 * - card_open: 큐피트 카드 오픈 (1하트)
 * - extend: 카드 연장 (3하트, 3일)
 */
export type HeartAction = 'card_open' | 'extend';

/**
 * 하트 소비 비용 정보
 */
export interface HeartCost {
  /** 액션 타입 */
  action: HeartAction;
  /** 소비되는 하트 개수 */
  hearts: number;
  /** 액션 설명 */
  description: string;
}

// ============================================================================
// Heart Balance (하트 잔액)
// ============================================================================

/**
 * 하트 잔액 정보
 */
export interface HeartBalance {
  /** 현재 보유 하트 */
  current: number;
  /** 사용 가능 여부 (충분한 잔액) */
  available: boolean;
}

/**
 * 하트 상태 레벨
 * - sufficient: 충분 (5개 이상)
 * - low: 부족 주의 (2~4개)
 * - critical: 긴급 충전 필요 (0~1개)
 */
export type HeartLevel = 'sufficient' | 'low' | 'critical';

// ============================================================================
// Card Open (카드 오픈)
// ============================================================================

/**
 * 카드 오픈 비용 정보
 */
export interface CardOpenCost {
  /** 소비되는 하트 */
  hearts: number;
  /** 오픈 전 하트 잔액 */
  beforeBalance: number;
  /** 오픈 후 하트 잔액 */
  afterBalance: number;
}

/**
 * 카드 연장 비용 정보
 */
export interface CardExtendCost {
  /** 소비되는 하트 */
  hearts: number;
  /** 연장 전 하트 잔액 */
  beforeBalance: number;
  /** 연장 후 하트 잔액 */
  afterBalance: number;
  /** 현재 만료 시간 */
  expiresAt: Date;
  /** 연장 후 만료 시간 */
  newExpiresAt: Date;
  /** 연장 후 만료 시간 (서버 KST raw string, 표시용) */
  newExpiresAtRaw: string;
  /** 연장 기간 (일) */
  extensionDays: number;
  /** 현재 연장 횟수 */
  currentExtensions: number;
  /** 최대 연장 횟수 */
  maxExtensions: number;
}

// ============================================================================
// Constants (상수)
// ============================================================================

/**
 * 리워드 금액 상수
 */
export const REWARDS = {
  /** 신규 가입 리워드 */
  SIGNUP: 7,
  /** 프로필 완성 리워드 */
  PROFILE_COMPLETE: 3,
} as const;

/**
 * 하트 소비 비용 상수
 */
export const HEART_COSTS = {
  /** 카드 오픈 비용 */
  CARD_OPEN: 1,
  /** 카드 연장 비용 */
  CARD_EXTENSION: 3,
} as const;

/**
 * 카드 연장 기간 (일)
 */
export const CARD_EXTENSION_DAYS = 3;

/**
 * 하트 레벨 임계값
 */
export const HEART_LEVEL_THRESHOLD = {
  /** 충분 (이상) */
  SUFFICIENT: 5,
  /** 부족 주의 (이상) */
  LOW: 2,
  /** 긴급 (미만) - 0~1개 */
  CRITICAL: 2,
} as const;
